import { useEffect, useState } from "react";
import { CheckCircle2, Mic, Volume2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { speak, cancelSpeech, speechRecognitionSupported, unlockAudio } from "@/lib/speech";
import { useMicCalibration, measureNoise, noiseLevel, ROOM_NOISE_KEY } from "@/lib/micCalibration";
import { QuietRoomCheck } from "@/components/QuietRoomCheck";
import { MicHelpButton } from "@/components/MicHelpButton";
import { MicPrompt, MicTips } from "@/components/MicCalibration";
import { LastMicCheck } from "@/components/LastMicCheck";
import { getMicStream, warmAudio } from "@/lib/recorder";
import { SPEAKER_OK_KEY } from "@/components/HardwareGate";

export const EquipmentCheck = ({ open, onOpenChange, onConfirm, title }) => {
  const [heard, setHeard] = useState(false);
  const { phase, level, transcript, stats, start: testMic, stop: stopMic, reset: resetMic } = useMicCalibration();
  const busy = phase === "quiet" || phase === "speak";

  useEffect(() => () => cancelSpeech(), []);
  const [speakerErr, setSpeakerErr] = useState("");
  const testSpeaker = async () => {
    unlockAudio(true);
    setHeard(false); setSpeakerErr("");
    const r = await speak("This is your Versant audio check. If you can hear this clearly, your speakers are ready.");
    if (r.ok) setHeard(true);
    else setSpeakerErr("No sound could be played. On a phone, switch off silent mode, turn the media volume up, then tap Play test sound again.");
  };
  const start = () => { unlockAudio(true); warmAudio(); stopMic(); if (heard) sessionStorage.setItem(SPEAKER_OK_KEY, String(Date.now())); getMicStream().catch(() => {}).finally(onConfirm); };
  const [room, setRoom] = useState({ state: "", noise: 0, level: 0 });
  const checkRoom = async () => {
    unlockAudio(true); stopMic();
    setRoom({ state: "measuring", noise: 0, level: 0 });
    const n = await measureNoise(3000, (level) => setRoom((r) => ({ ...r, level })));
    if (n == null) return start();
    sessionStorage.setItem(ROOM_NOISE_KEY, JSON.stringify({ noise: n, at: Date.now() }));
    const lv = noiseLevel(n);
    if (lv === "ok") return start();
    setRoom({ state: lv, noise: n, level: 0 });
  };
  // A calibration just run in this window already measured the room
  const ready = () => {
    if (room.state === "warn" || room.state === "bad") return start();
    if (stats && !stats.error && stats.noise != null) {
      sessionStorage.setItem(ROOM_NOISE_KEY, JSON.stringify({ noise: stats.noise, at: Date.now() }));
      return noiseLevel(stats.noise) === "ok" ? start() : setRoom({ state: noiseLevel(stats.noise), noise: stats.noise, level: 0 });
    }
    checkRoom();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { resetMic(); setRoom({ state: "", noise: 0, level: 0 }); } onOpenChange(o); }}>
      <DialogContent data-testid="equipment-check-modal" className="max-h-[92vh] max-w-lg overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Audio & Mic Equipment Check</DialogTitle>
          <DialogDescription>{title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {phase === "idle" && <LastMicCheck />}
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="mb-2 flex items-center gap-2 font-semibold text-slate-800"><Volume2 className="h-4 w-4 text-indigo-600" />1. Speakers / headset</div>
            <button data-testid="equipment-test-speaker-btn" onClick={testSpeaker} className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700">Play test sound</button>
            {heard && <span data-testid="equipment-speaker-ok" className="ml-3 inline-flex items-center gap-1 text-sm text-emerald-600"><CheckCircle2 className="h-4 w-4" />Played</span>}
            {speakerErr && <p data-testid="equipment-speaker-error" className="mt-2 text-xs text-rose-600">{speakerErr}</p>}
          </div>
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="mb-2 flex items-center gap-2 font-semibold text-slate-800"><Mic className="h-4 w-4 text-indigo-600" />2. Microphone</div>
            <button data-testid="equipment-test-mic-btn" onClick={testMic} disabled={busy} className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-50">{busy ? "Calibrating…" : phase === "done" || phase === "error" ? "Test again" : "Test & calibrate microphone"}</button>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200"><div data-testid="equipment-mic-level" className={`h-full transition-[width] duration-75 ${level > 85 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${level}%` }} /></div>
            <MicPrompt phase={phase} />
            {transcript && <p data-testid="equipment-transcript" className="mt-2 text-sm text-slate-600">Heard: "{transcript}"</p>}
            {!speechRecognitionSupported && phase === "idle" && <p className="mt-2 text-xs text-amber-600">Speaking answers are recorded by the microphone and transcribed automatically in any modern browser.</p>}
            {phase === "done" || phase === "error" ? <MicTips stats={stats} /> : null}
            {phase === "error" && <MicHelpButton state={{ kind: stats?.error || "error" }} className="mt-2" />}
          </div>
          <QuietRoomCheck state={room.state} level={room.level} noise={room.noise} onRetry={checkRoom} onContinue={start} />
          <p className="text-xs text-slate-500">Exam rules: audio plays once, no replay or pause, timers cannot be stopped, and answers auto-save when time runs out.</p>
          <button data-testid="equipment-start-exam-btn" onClick={ready} disabled={room.state === "measuring" || busy} className="w-full rounded-full bg-indigo-600 py-3 text-sm font-bold text-white transition-colors hover:bg-indigo-700 disabled:opacity-50">{room.state === "measuring" ? "Checking room noise…" : "I'm ready — start the exam"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
